/**
 * Shared grid-layout maths used by the player grid (`NewTriLayoutContainer`,
 * `Full` mode) and the generic `Grid` component.
 *
 * The core idea is the standard "fit N tiles of a common aspect ratio into a
 * W×H box, maximizing tile size" search (the approach video conferencing apps
 * like Meet/Zoom use): try every column count, keep the one that yields the
 * largest tile while respecting the target ratio.
 */

const LETTERS = "abcdefghijklmnopqrstuvwxyz";

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export interface OptimalGridLayout {
  cols: number;
  rows: number;
  cellW: number;
  cellH: number;
}

/**
 * Pick the rows/columns split that maximizes tile size for `count` tiles of
 * aspect `ratio` inside a `availableW × availableH` box (gaps accounted for).
 */
export const computeOptimalLayout = (
  count: number,
  availableW: number,
  availableH: number,
  gap: number,
  ratio: number,
): OptimalGridLayout => {
  let bestCols = 1;
  let bestCellW = 0;
  let bestCellH = 0;

  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const maxCellW = (availableW - gap * (cols - 1)) / cols;
    const maxCellH = (availableH - gap * (rows - 1)) / rows;

    let cellW = maxCellW;
    let cellH = cellW / ratio;

    if (cellH > maxCellH) {
      cellH = maxCellH;
      cellW = cellH * ratio;
    }

    if (cellW > bestCellW) {
      bestCellW = cellW;
      bestCellH = cellH;
      bestCols = cols;
    }
  }

  const bestRows = Math.ceil(count / bestCols);
  return { cols: bestCols, rows: bestRows, cellW: bestCellW, cellH: bestCellH };
};

/**
 * Build a CSS `grid-template-areas` matrix (named single-letter areas) for
 * `count` tiles laid out in `cols × rows`, with the (possibly incomplete) last
 * row centered.
 *
 * Centering uses a doubled column resolution: each tile spans 2 sub-columns, so
 * a leading/trailing offset of `(cols - itemsInRow)` empty sub-columns centers
 * the last row without any half-cell drift. Returns rows of length `cols * 2`;
 * pair this with `grid-template-columns: repeat(cols * 2, minmax(0, 1fr))`.
 * Empty cells are the CSS `.` placeholder.
 */
export const buildCenteredGridAreas = (count: number, cols: number, rows: number): string[][] => {
  const grid: string[][] = [];
  for (let r = 0; r < rows; r++) {
    const isLast = r === rows - 1;
    const itemsInRow = isLast ? count - r * cols : cols;
    const lead = isLast ? cols - itemsInRow : 0;
    const line: string[] = [];
    for (let i = 0; i < lead; i++) line.push(".");
    for (let c = 0; c < itemsInRow; c++) {
      const letter = LETTERS[r * cols + c];
      line.push(letter, letter);
    }
    for (let i = 0; i < lead; i++) line.push(".");
    grid.push(line);
  }
  return grid;
};

/**
 * Largest number of tiles that keeps each tile readable in a `W × H` box: the
 * biggest N for which the optimal layout's smallest tile side stays above a
 * minimum size. The threshold is scaled down on high-density screens (a CSS px
 * is sharper there), clamped so phones don't pack a swarm of tiny tiles.
 *
 * Falls back to 9 when the container hasn't been measured yet.
 */
export const computeAutoPageSize = (
  W: number,
  H: number,
  ratio: number,
  gap: number,
  autoTileMinPx: number,
  cap = 49,
): number => {
  if (W <= 0 || H <= 0) return 9;
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  const threshold = autoTileMinPx / clamp(dpr, 1, 2);
  let best = 1;
  for (let n = 1; n <= cap; n++) {
    const { cellW, cellH } = computeOptimalLayout(n, W, H, gap, ratio);
    if (Math.min(cellW, cellH) < threshold) break;
    best = n;
  }
  return Math.max(1, best);
};
